import { useState, useEffect, Fragment, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./styles.sass";
import assets from "../../assets";
import useHandler from "./handler";
import { ToggleButton } from "../../role-based-code/toggle-button";
import { QuizHero } from "../../components/quiz-hero";
import { CommonPopup } from "../../components/pop-up";
import { AdminRoundResult } from "../../components/admin-round-results";
import { QuizHistroyDetails } from "../quiz-history";
import { LatexRenderer } from "../../components/latex-renderer";
import { useAntiCheat, AntiCheatModal, AutoSubmittedModal, AntiCheatRulesModal, useLiveQuizLock, LiveQuizFullscreenGuard } from "../../hooks/useAntiCheat";
import { ImageZoomModal, QuizImagePreview } from "../../components/image-zoom-modal";

const PRACTICE_CONTENT = [
  "No time pressure",
  "Instant feedback & explanations",
  "Personalized learning path",
];

const JOIN_ROOM_CONTENT = [
  "Live multiplayer experience",
  "Timer-based challenges",
  "Global leaderboard & scoring",
];

const QuestionCardContent = ({
  q,
  qIndex,
  currentQIndex,
  isFullySubmitted,
  isAnsweredQ,
  isCorrectQ,
  selOpt,
  mode,
  handleOptionSelect,
  showStack,
  isReviewMode,
  setCurrentQIndex,
  setShowPaletteModal,
  questions,
  questionDetails,
  onZoomImage,
}) => {
  const scrollRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkScroll = () => {
      const isScrollable = el.scrollHeight > el.clientHeight + 10; // Threshold reduced to 10px
      const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 15;
      setShowScrollHint(isScrollable && !isAtBottom);
    };

    // Check on mount and resize
    checkScroll();
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);

    el.addEventListener("scroll", checkScroll);
    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", checkScroll);
    };
  }, [q?.question, q?.options]);

  const handleScrollDown = () => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      top: 200,
      behavior: "smooth",
    });
  };

  return (
    <Fragment>
      <section className="question-section" ref={scrollRef}>
        <h3 className="question-title"><LatexRenderer text={q?.question} /></h3>

        {q?.imageUrls && q.imageUrls.length > 0 && (
          <div className="ws_q_image_container">
            {q.imageUrls.map((imgUrl, i) => (
              <QuizImagePreview
                key={i}
                src={imgUrl}
                alt={`Question Diagram ${i + 1}`}
                title={`Question ${qIndex + 1} Diagram`}
                onZoom={onZoomImage}
              />
            ))}
          </div>
        )}

        {(isFullySubmitted || (isAnsweredQ && !mode)) && isCorrectQ ? (
          <div className="answer-feedback correct">
            <span>Correct Answer!</span>
            <div className="feedback_icon correct">✓</div>
          </div>
        ) : (
          ""
        )}

        {(isFullySubmitted || (isAnsweredQ && !mode)) && !isCorrectQ && (
          <section className="explaination_full_wrapper">
            <img src={assets.ic_wrong_close} alt="wrong" />

            <section className="explaination_wrapper">
              <h4>Incorrect</h4>

              <section className="explaination_container">
                <h5>Explanation:</h5>
                <h6><LatexRenderer text={questionDetails[qIndex]?.EXPLANATION} /></h6>
              </section>
            </section>
          </section>
        )}

        <section className="options-section">
          {q?.options?.map((option, index) => (
            <label
              key={index}
              className={`option_quiz ${(() => {
                const isSubmitted = isFullySubmitted || (isAnsweredQ && !mode);

                if (!isSubmitted) {
                  return selOpt === index ? "selected" : "";
                }

                // // Correct answer -> green
                // if (index === q.correctOptionIndex) {
                //   return "option-correct";
                // }

                // // Selected wrong answer -> red
                // if (selOpt === index && index !== q.correctOptionIndex) {
                //   return "option-wrong";
                // }

                if (q?.options[index] === q?.answer) {
                  return "option-correct";
                }

                if (selOpt === index && q?.options[index] !== q?.answer) {
                  return "option-wrong";
                }

                return "";
              })()}`}
            >
              <input
                type="radio"
                name={`quiz-option-${qIndex}`}
                checked={selOpt === index}
                disabled={
                  isFullySubmitted ||
                  (selOpt !== undefined && !mode) ||
                  (showStack && qIndex !== currentQIndex)
                }
                onChange={() => handleOptionSelect(index, qIndex)}
              />
              <section className="option-text">
                <span className="option-letter">
                  {String.fromCharCode(65 + index)}.{" "}
                </span>
                <LatexRenderer text={option} />
                {q?.optionImages?.[String.fromCharCode(65 + index)] && (
                  <div className="ws_opt_img_wrapper">
                    <QuizImagePreview
                      src={q.optionImages[String.fromCharCode(65 + index)]}
                      alt={`Option ${String.fromCharCode(65 + index)}`}
                      title={`Question ${qIndex + 1} - Option ${String.fromCharCode(65 + index)}`}
                      isOption
                      onZoom={onZoomImage}
                    />
                  </div>
                )}
              </section>
            </label>
          ))}
        </section>
      </section>

      <div
        className={`scroll_indicator_container ${showScrollHint ? "visible" : "hidden"}`}
        onClick={handleScrollDown}
      >
        <div className="scroll_hint">
          <span>Scroll for more</span>
          <img src={assets.ic_chevron_down} alt="scroll down" />
        </div>
      </div>

      {!isReviewMode && !isFullySubmitted && (
        <div className="quiz-nav-footer">
          <button
            className="prev_btn_wrapper"
            onClick={(e) => {
              e.stopPropagation();
              if (qIndex > 0) {
                setCurrentQIndex(qIndex - 1);
              }
            }}
            disabled={qIndex === 0}
          >
            <img src={assets.ic_left_arrow_blue} alt="ic_left_arrow_blue" />
            <h5>Previous Question</h5>
          </button>
          <button
            className={
              qIndex === questions?.length - 1
                ? "end_quiz_btn_inline"
                : "nexts_btn_wrapper"
            }
            onClick={(e) => {
              e.stopPropagation();
              if (qIndex === questions?.length - 1) {
                setShowPaletteModal(true);
              } else {
                setCurrentQIndex(qIndex + 1);
              }
            }}
          >
            <h5>
              {qIndex === questions?.length - 1 ? "End Quiz" : "Next Question"}
            </h5>
            <img src={assets.ic_arrow_white} alt="ic_arrow_white" />
          </button>
        </div>
      )}
    </Fragment>
  );
};

const QuestionPalettePage = ({
  questions,
  selectedAnswers,
  getQuestionStatus,
  setCurrentQIndex,
  setShowPaletteModal,
  setIsQuizActive,
  onSubmitQuiz,
  submitLoading,
  setIsReviewMode,
}) => {
  const unansweredCount = questions?.filter(
    (_, i) => selectedAnswers[i] === undefined,
  ).length;

  return (
    <div className="palette_page_container">
      <div className="palette_page_content">
        <div className="palette_modal_header_main">
          <h3>Question Palette</h3>
          <p>
            You have {String(unansweredCount).padStart(2, "0")} unanswered
            questions. Click on any question number to review.
          </p>
        </div>

        <div className="palette_modal_subheader">
          <h4>Question Palette</h4>
          <div className="q_count_badge_premium">
            {questions?.length} Questions
          </div>
        </div>

        <div className="palette_modal_grid">
          {questions.map((_, index) => {
            const isAnswered = selectedAnswers[index] !== undefined;
            const className = isAnswered
              ? "palette_circle answered"
              : "palette_circle unoccupied";

            return (
              <div
                key={index}
                className={className}
                onClick={() => {
                  setCurrentQIndex(index);
                  setShowPaletteModal(false);
                  setIsReviewMode(true);
                }}
              >
                {String(index + 1).padStart(2, "0")}
              </div>
            );
          })}
        </div>

        <div className="palette_modal_legend">
          <div className="legend_item_premium">
            <div className="status_indicator answered"></div>
            <span>Answered</span>
          </div>
          <div className="legend_item_premium">
            <div className="status_indicator unoccupied"></div>
            <span>Unanswered</span>
          </div>
        </div>

        <div className="palette_modal_actions">
          <button
            className="p_cancel_btn"
            onClick={() => setShowPaletteModal(false)}
          >
            Cancel
          </button>
          <button
            className="p_submit_btn"
            onClick={() => {
              setShowPaletteModal(false);
              setIsQuizActive(false);
              onSubmitQuiz();
              setIsReviewMode(true);
            }}
          >
            {submitLoading && <div className="btn_spinner"></div>}
            Submit Quiz
          </button>
        </div>
      </div>
    </div>
  );
};

const QuestionPaletteSidebar = ({
  questions,
  selectedAnswers,
  getQuestionStatus,
  currentQIndex,
  setCurrentQIndex,
  isQuizSubmitted,
  onSubmitQuiz,
  submitLoading,
}) => {
  return (
    <section className="palette_sidebar_premium">
      <div className="sidebar_palette_header">
        <span>Question Palette</span>
        <div className="q_badge_mini">{questions?.length} Questions</div>
      </div>

      <div className="sidebar_palette_grid">
        {questions.map((_, index) => {
          const status = getQuestionStatus(index);
          const isAnsweredQ = selectedAnswers[index] !== undefined;

          let className = "palette_circle_mini";
          if (isAnsweredQ) {
            className += " answered";
            if (index === currentQIndex) className += " current_answered";
          } else if (index === currentQIndex) {
            className += " current";
          } else if (status === "not-answered") {
            className += " not_answered";
          } else {
            className += " unoccupied";
          }

          return (
            <div
              key={index}
              className={className}
              onClick={() => setCurrentQIndex(index)}
            >
              {String(index + 1).padStart(2, "0")}
            </div>
          );
        })}
      </div>

      <div className="sidebar_status_legend">
        <div className="legend_item_mini">
          <div className="status_dot current"></div>
          <span>Current</span>
        </div>
        <div className="legend_item_mini">
          <div className="status_dot answered"></div>
          <span>Answered</span>
        </div>
        <div className="legend_item_mini">
          <div className="status_dot unoccupied"></div>
          <span>Unanswered</span>
        </div>
      </div>
      {!isQuizSubmitted && (
        <button
          style={{
            width: "100%",
            marginTop: "auto",
            background: "#0f172a",
            color: "#fff",
            padding: "14px",
            borderRadius: "8px",
            fontWeight: "bold",
            cursor: "pointer",
            border: "none",
          }}
          onClick={onSubmitQuiz}
        >
          {submitLoading ? "Submitting..." : "Submit Quiz"}
        </button>
      )}
    </section>
  );
};

const QuizHSlider = ({
  questions,
  renderCard,
  currentQIndex,
  setCurrentQIndex,
}) => {
  const trackRef = useRef(null);
  const rafIdRef = useRef(null);
  const cooldownRef = useRef(false);
  const wheelAccRef = useRef(0); // accumulated deltaX within one gesture
  const wheelTimerRef = useRef(null); // debounce to reset accumulator on silence
  const [isReady, setIsReady] = useState(false);

  const ease = (t) => 1 - Math.pow(1 - t, 3);

  const animateTo = useCallback((track, targetLeft) => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    const start = track.scrollLeft;
    const dist = targetLeft - start;
    if (Math.abs(dist) < 1) {
      cooldownRef.current = false;
      return;
    }
    const duration = 360;
    const startTime = performance.now();
    const step = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      track.scrollLeft = start + dist * ease(p);
      if (p < 1) {
        rafIdRef.current = requestAnimationFrame(step);
      } else {
        track.scrollLeft = targetLeft;
        rafIdRef.current = null;
        setTimeout(() => {
          cooldownRef.current = false;
        }, 80);
      }
    };
    rafIdRef.current = requestAnimationFrame(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToIndex = useCallback(
    (idx, track) => {
      const t = track || trackRef.current;
      if (!t) return;
      const card = t.children[idx];
      if (!card) return;
      const target =
        t.clientWidth > 0
          ? card.offsetLeft - (t.clientWidth - card.offsetWidth) / 2
          : idx === 0
            ? 0
            : card.offsetLeft;
      animateTo(t, Math.max(0, target));
    },
    [animateTo],
  );

  useEffect(() => {
    setIsReady(false);
    cooldownRef.current = true;
    goToIndex(currentQIndex);
    const timer = setTimeout(() => {
      setIsReady(true);
      cooldownRef.current = false;
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length]);

  useEffect(() => {
    if (!isReady) return;
    cooldownRef.current = true;
    goToIndex(currentQIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!isReady || cooldownRef.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.85) {
            const idx = Array.from(track.children).indexOf(entry.target);
            if (idx >= 0) setCurrentQIndex(idx);
          }
        });
      },
      { root: track, threshold: 0.85 },
    );
    Array.from(track.children).forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, [questions.length, setCurrentQIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onWheel = (e) => {
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      if (absY >= absX) return;
      e.preventDefault();
      // Debounce: reset accumulator 200ms after gesture ends
      clearTimeout(wheelTimerRef.current);
      wheelTimerRef.current = setTimeout(() => {
        wheelAccRef.current = 0;
      }, 200);
      // While animating, swallow all events without accumulating
      if (cooldownRef.current) return;
      wheelAccRef.current += e.deltaX;
      // Require 60px of intentional scroll before advancing one card
      if (Math.abs(wheelAccRef.current) < 60) return;
      const direction = wheelAccRef.current > 0 ? 1 : -1;
      wheelAccRef.current = 0;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      setCurrentQIndex((prev) => {
        const next =
          direction > 0 ? Math.min(prev + 1, maxIdx) : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else {
          setTimeout(() => {
            cooldownRef.current = false;
          }, 80);
        }
        return next;
      });
    };
    track.addEventListener("wheel", onWheel, { passive: false });
    return () => track.removeEventListener("wheel", onWheel);
  }, [questions.length, goToIndex, setCurrentQIndex]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      const track = trackRef.current;
      setCurrentQIndex((prev) => {
        const next =
          e.key === "ArrowRight"
            ? Math.min(prev + 1, maxIdx)
            : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else {
          setTimeout(() => {
            cooldownRef.current = false;
          }, 80);
        }
        return next;
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [questions.length, goToIndex, setCurrentQIndex]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let startX = 0,
      startY = 0;
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const dx = startX - e.changedTouches[0].clientX;
      const dy = startY - e.changedTouches[0].clientY;
      if (Math.abs(dx) < Math.abs(dy) || Math.abs(dx) < 30) return;
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      setCurrentQIndex((prev) => {
        const next =
          dx > 0 ? Math.min(prev + 1, maxIdx) : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else {
          setTimeout(() => {
            cooldownRef.current = false;
          }, 80);
        }
        return next;
      });
    };
    track.addEventListener("touchstart", onTouchStart, { passive: true });
    track.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      track.removeEventListener("touchstart", onTouchStart);
      track.removeEventListener("touchend", onTouchEnd);
    };
  }, [questions.length, goToIndex, setCurrentQIndex]);

  return (
    <section className="quiz_hslider_wrapper">
      <div className="quiz_hslider_track" ref={trackRef}>
        {questions.map((_, qIndex) => (
          <div
            className={`quiz_hslider_card${qIndex === currentQIndex ? " is_active" : ""}`}
            key={qIndex}
          >
            <div className="quiz_hslider_card_inner">{renderCard(qIndex)}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

const QuizJoinInitialContent = ({
  waitingCall,
  roomCode,
  nickName,
  setWaitingCall,
  onChangeRoom,
  joinRoom,
  setJoinQuiz,
}) => {
  const navigate = useNavigate();
  const handleJoinClick = () => {
    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch((err) => console.log("Fullscreen request error:", err));
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } catch (e) {
      console.log("Fullscreen error:", e);
    }
    document.body.classList.add("hide_sidebar");
    joinRoom();
  };

  return (
    <section className="join_quiz_entry_wrapper">
      <button
        className="join_quiz_back_btn"
        onClick={() => {
          setJoinQuiz(false);
          setWaitingCall(false);
          document.body.classList.remove("hide_sidebar");
          if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
          }
          const currentPath = window.location.pathname;
          const basePath = currentPath.split("/quiz")[0];
          const targetDashboardRoute = basePath === "" ? "/dashboard" : `${basePath}/dashboard`;
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate(targetDashboardRoute);
          }
        }}
      >
        <svg
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        <span>Back</span>
      </button>
      <img src={assets.ic_join_rocket} alt="ic_join_rocket" />
      <h3>Join the Quiz</h3>
      <p>Enter the room code your teacher shared to jump in.</p>
      <section className="join_quiz_input_container">
        <h4>Nickname</h4>
        <section className="join_quiz_input_wrapper">
          <img src={assets.ic_mood_emoji} alt="ic_mood_emoji" />
          <input
            placeholder="e.g. QuizMaster99"
            name="nickName"
            value={nickName}
            onChange={onChangeRoom}
          />
        </section>
      </section>
      <section className="join_quiz_input_container">
        <h4>Room Code</h4>
        <section className="join_quiz_input_code_wrapper">
          <input
            placeholder="e.g. 84920"
            name="roomCode"
            value={roomCode}
            onChange={onChangeRoom}
            maxLength={6}
            type="number"
          />
        </section>
      </section>
      <section className="practice_btn_wrapper" onClick={handleJoinClick}>
        <h4>Join Room</h4>
        <img src={assets.ic_right_arrow} />
      </section>
    </section>
  );
};

const QuizLobbyContent = ({
  players,
  roomCode,
  nickName,
  setJoinQuiz,
  setWaitingCall,
}) => {
  return (
    <section className="chat_join_lobby_container">
      <div className="lobby_hero_card">
        <div className="chat_lobby_room_container">
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          <span className="room_code_label">ROOM CODE:</span>
          <span className="room_code_val">{roomCode}</span>
        </div>

        <div className="round_container">
          <div className="first_round_cricle">
            <div className="second_round_cricle">
              <div className="third_round_cricle">
                <h1>{nickName?.charAt(0)?.toUpperCase()}</h1>
              </div>
            </div>
          </div>
        </div>

        <h2 className="nick_name_text_wrapper">
          {nickName?.charAt(0)?.toUpperCase() + nickName?.slice(1)}
        </h2>

        <div className="room_loading_content">
          <div className="orange_dot_loader">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <h6>Waiting for teacher to start...</h6>
        </div>

        <button
          className="leave_room_btn_container"
          onClick={() => {
            setJoinQuiz(false);
            setWaitingCall(false);
            document.body.classList.remove("hide_sidebar");
            if (document.fullscreenElement) {
              document.exitFullscreen().catch(() => {});
            }
          }}
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span>Leave Room</span>
        </button>
      </div>

      <div className="chat_lobby_static_wrapper">
        <div className="lobby_static_card_wrapper">
          <div className="lobby_card_icon_badge tip">
            <img src={assets.ic_quick_tip} alt="Quick Tip" />
          </div>
          <div className="lobby_card_text">
            <h4>Quick Tip</h4>
            <p>
              Read all options carefully before answering. Some questions might
              have tricky wording!
            </p>
          </div>
        </div>

        <div className="lobby_static_card_wrapper">
          <div className="lobby_card_icon_badge fair">
            <img src={assets.ic_fair_play} alt="Fair Play" />
          </div>
          <div className="lobby_card_text">
            <h4>Fair Play</h4>
            <p>
              Keep your answers to yourself. Points are awarded for speed and
              accuracy.
            </p>
          </div>
        </div>

        <div className="lobby_static_card_wrapper">
          <div className="lobby_card_icon_badge wifi">
            <img src={assets.ic_wifi_green} alt="Connection Stable" />
          </div>
          <div className="lobby_card_text">
            <h4>Connection Stable</h4>
            <p>Ping: 24ms. You're fully synced with the host and ready to go.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const QuizReviewContent = ({
  questions,
  selectedAnswers,
  setAsyncCurrentIndex,
  setShowReviewPage,
  setEditingFromReview,
  onSubmitQuiz,
  showReviewPage,
  asyncQuizSubmit,
}) => {
  return (
    <section className="quiz_review_wrapper">
      <section className="quiz_review_heading_wrapper">
        <h4>Review Your Answers</h4>

        <h6>
          Review your answers before final submission. You can still make
          changes.
        </h6>
      </section>

      <section className="quiz_review_main_content_wrapper">
        {questions?.map((question, index) => {
          const selectedValue = selectedAnswers[index];
          const isAnswered = selectedValue !== undefined && selectedValue !== null && String(selectedValue).trim() !== "";
          const isShort = question?.isShort || question?.type === "SHORT" || question?.questionType === "SHORT";

          const selectedOption = isAnswered
            ? (isShort ? String(selectedValue) : question?.options?.[selectedValue] || "Answered")
            : "Not Answered";

          return (
            <section
              key={question?.questionId || index}
              className="quiz_review_card"
            >
              <h4>
                {index + 1}. <LatexRenderer text={question?.question} />
              </h4>

              <section className="quiz_review_answer_container">
                <h6>Your Answer</h6>

                <section className="quiz_review_answer">
                  <section className="review_answer_section">
                    <h5>
                      {isAnswered
                        ? isShort
                          ? "ANS"
                          : String.fromCharCode(65 + selectedValue)
                        : "-"}
                    </h5>
                  </section>

                  <h5><LatexRenderer text={selectedOption} /></h5>
                </section>
              </section>

              <section
                className="quiz_review_edit_wrapper"
                onClick={() => {
                  if (setEditingFromReview) setEditingFromReview(true);
                  setAsyncCurrentIndex(index);
                  setShowReviewPage(false);
                }}
              >
                <img src={assets.ic_pencil_blue} alt="ic_pencil_blue" />

                <h4>Edit Answer</h4>
              </section>
            </section>
          );
        })}
      </section>

      <section className="quiz_review_submit_quiz" onClick={asyncQuizSubmit}>
        <h4>Submit Quiz</h4>

        <img src={assets.ic_right_arrow} alt="ic_right_arrow_white" />
      </section>
    </section>
  );
};

export const Quiz = () => {
  const {
    user,
    openDropdown,
    subjectRef,
    chapterRef,
    selectedChapter,
    setSelectedChapter,
    selectedSubject,
    panelOpen,
    chapters,
    subjects,
    isStartEnabled,
    currentQuestion,
    selectedOption,
    questions,
    currentQIndex,
    questionDetails,
    isAnswered,
    isCorrect,
    markDetails,
    showMarks,
    setShowMarks,
    isQuizActive,
    selectedLesson,
    selectedPart,
    selectedUnit,
    partRef,
    lessonRef,
    unitRef,
    questionRef,
    parts,
    lessons,
    units,
    isLoading,
    selectChapters,
    mediums,
    mediumRef,
    selectedMedium,
    selectLessons,
    hidePanals,
    mode,
    selectedQuestion,
    numberOfQuestion,
    selectedAnswers,
    submitLoading,
    timeRemaining,
    // new feature
    quizInitial,
    joinQuiz,
    waitingCall,
    roomCode,
    nickName,
    players,
    wsQuestion,
    wsQuestionIndex,
    wsTotalQuestions,
    wsLeaderboard,
    wsFinalScores,
    wsParticipants,
    wsQuizStarted,
    wsQuizEnded,
    wsSelectedAnswer,
    wsReviewMode,
    wsQuizSubmitted,
    wsQuizFinalResults,
    isAsyncQuiz,
    asyncCurrentQuestion,
    asyncCurrentIndex,
    asyncSelectedAnswers,
    showReviewPage,
    asyncQuestions,
    leaderboardData,
    showAsyncLeaderboard,
    asyncLeaderboard,
    quizHistorySessions,
    showAsyncHistory,
    wsTimeRemaining,
    wsSubmitted,
    showAdminRoundResult,
    adminRoundResult,
    setShowAdminRoundResult,
    setWsSubmitted,
    setRoomDetails,
    setAsyncQuestions,
    setAsyncCurrentIndex,
    setAsyncSelectedAnswers,
    setShowReviewPage,
    setAsyncLeaderboard,
    setShowAsyncLeaderboard,
    setShowAsyncHistory,
    setIsAsyncQuiz,
    setWsTimeRemaining,
    handleWsAnswer,
    setWsFinalScores,
    setWsQuestion,
    setWsQuizStarted,
    setWsQuizEnded,
    asyncQuizSubmit,
    apiJoinRoomHistoryDetails,
    handleAsyncAnswer,
    handleAsyncNext,
    joinRoom,
    setWsReviewMode,
    setWsQuizSubmitted,
    setWsSelectedAnswer,
    setQuizInitial,
    setJoinQuiz,
    setPlayers,
    onChangeRoom,
    setWaitingCall,
    setTimeRemaining,
    setSelectedQuestion,
    setCurrentQIndex,

    setSelectedLesson,
    setSelectedPart,
    setSelectedUnit,
    setSelectedChapters,
    setIsReviewMode,
    isReviewMode,
    setPanelOpen,
    setOpenDropdown,
    setSelectedSubject,
    onStartQuiz,
    handleNext,
    handlePrevious,
    handleOptionSelect,
    onSubmitQuiz,
    onCancel,
    onRetakeQuiz,
    setIsQuizActive,
    getQuizDuration,
    setSelectedMedium,
    setSelectedLessons,
    setHidePanals,
    onToggleQuizBtn,
    getQuestionStatus,
    showSetupPopup,
    setShowSetupPopup,
    showPaletteModal,
    setShowPaletteModal,
    editingFromReview,
    setEditingFromReview,
  } = useHandler();

  const [zoomModalImage, setZoomModalImage] = useState(null);

  const isAntiCheatActive =
    (isAsyncQuiz && !showReviewPage && !showAsyncLeaderboard) ||
    (isQuizActive && !showReviewPage && !showMarks && !showAsyncLeaderboard && !showAsyncHistory);

  // Silent fullscreen lock & key restriction for Live Quiz (no popups, no warnings, no auto-submit)
  const { isNeedsFullscreen, tryFullscreen } = useLiveQuizLock(wsQuizStarted && !wsQuizEnded && !wsSubmitted);

  const {
    warnings: antiCheatWarnings,
    showWarningModal: showAntiCheatModal,
    warningMessage: antiCheatMessage,
    isAutoSubmitted,
    autoSubmitReason,
    showRulesModal,
    acknowledgeRules,
    reEnterFullscreen,
    closeAutoSubmitModal,
  } = useAntiCheat({
    isActive: isAntiCheatActive,
    maxWarnings: 3,
    onAutoSubmit: () => {
      if (isAsyncQuiz) {
        asyncQuizSubmit();
      }
    },
  });

  const showJoinScreen =
    joinQuiz &&
    !waitingCall &&
    !wsQuizStarted &&
    !asyncCurrentQuestion &&
    !wsQuestion &&
    !showAsyncLeaderboard;

  const totalQuestions = questions?.length;
  const isFullySubmitted = !!markDetails?.DETAILS?.length;

  const [slideDirection, setSlideDirection] = useState("right");
  const [animKey, setAnimKey] = useState(0);

  const QuizSetupPopup = () => {
    const durationMin = getQuizDuration(selectLessons, selectedQuestion) / 60;
    // const qCount =
    //   selectLessons?.includes("All Lessons") || selectLessons?.length > 1
    //     ? 25
    //     : 10;
    const qCount = selectedQuestion || 10;

    return (
      <div className="quiz_setup_overlay">
        <div className="quiz_setup_card">
          <div className="quiz_setup_header">
            <h3>Quiz Setup</h3>
            <p>Review your quiz configuration</p>
          </div>

          <div className="quiz_setup_body">
            <div className="setup_section">
              <div className="setup_icon_wrapper subject_p_icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </div>
              <div className="setup_info">
                <span className="setup_label">Subject</span>
                <span className="setup_value">
                  {selectedPart} - {selectedUnit}
                </span>
              </div>
            </div>

            <div className="setup_section">
              <div className="setup_icon_wrapper chapter_p_icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <div className="setup_info">
                <span className="setup_label">Selected Lessons</span>
                <ul className="setup_list_p">
                  {(selectLessons || []).map((chapter, idx) => (
                    <li key={idx}>{chapter}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="setup_stats_row">
              <div className="setup_stat_box questions_box_p">
                <div className="stat_icon quiz_icon_p">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div className="stat_labels">
                  <span className="stat_label">QUESTIONS</span>
                  <span className="stat_value">{qCount}</span>
                </div>
              </div>

              <div className="setup_stat_box duration_box_p">
                <div className="stat_icon time_icon_p">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="stat_labels">
                  <span className="stat_label">DURATION</span>
                  <span className="stat_value">{durationMin}min</span>
                </div>
              </div>
            </div>

            <div className="setup_note_p">
              <div className="note_icon_p">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p>
                Once you start the quiz, the timer will begin automatically.
                Make sure you're ready! Before final submission, you can review
                your answers and complete any unanswered questions.
              </p>
            </div>
          </div>

          <div className="quiz_setup_footer">
            <button
              className="setup_cancel_btn"
              onClick={() => setShowSetupPopup(false)}
            >
              Cancel
            </button>
            <button
              className="setup_start_btn"
              onClick={() => {
                setShowSetupPopup(false);
                setHidePanals(true);
                setIsQuizActive(true);
                onStartQuiz();
              }}
            >
              Start Quiz
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const isTestActive =
      waitingCall ||
      wsQuizStarted ||
      isAsyncQuiz ||
      isQuizActive ||
      showAsyncLeaderboard ||
      showReviewPage ||
      wsQuizEnded ||
      showAsyncHistory ||
      (asyncLeaderboard && asyncLeaderboard.length > 0);

    if (isTestActive) {
      document.body.classList.add("hide_sidebar");
    } else {
      document.body.classList.remove("hide_sidebar");
    }

    return () => {
      document.body.classList.remove("hide_sidebar");
    };
  }, [
    waitingCall,
    wsQuizStarted,
    isAsyncQuiz,
    isQuizActive,
    showAsyncLeaderboard,
    showReviewPage,
    wsQuizEnded,
    showAsyncHistory,
    asyncLeaderboard,
  ]);

  useEffect(() => {
    if (questions.length > 0 && isQuizActive) {
      const duration = getQuizDuration(selectLessons, selectedQuestion);
      setTimeRemaining(duration);
    }
  }, [questions.length, isQuizActive]);

  useEffect(() => {
    if (!isQuizActive || questions.length === 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsQuizActive(false);
          onSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isQuizActive, questions.length]);

  useEffect(() => {
    if (!wsQuizStarted || !wsQuestion) return;

    setWsTimeRemaining(15);

    const timer = setInterval(() => {
      setWsTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);

          // auto submit only once
          if (!wsSubmitted && wsSelectedAnswer !== null) {
            handleWsAnswer(wsSelectedAnswer);
          }

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [wsQuestionIndex, wsSubmitted, wsSelectedAnswer]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const correctCount = markDetails?.DETAILS?.filter(
    ({ IS_CORRECT }) => IS_CORRECT === true,
  ).length;
  const wrongCount = markDetails?.DETAILS?.filter(
    ({ USER_ANSWER, IS_CORRECT }) => USER_ANSWER && IS_CORRECT === false,
  ).length;
  const notAttemptedCount = markDetails?.DETAILS?.filter(
    (USER_ANSWER) => !USER_ANSWER,
  ).length;

  const scorePercentage = Math.round(
    (markDetails?.TOTAL_MARKS / markDetails?.MAX_MARKS) * 100,
  );

  const notAnswerdCount = markDetails?.NOT_ATTEMPTED_COUNT;

  const renderScoreCardDetails = (item, index) => {
    const {
      QUESTION,
      IS_CORRECT,
      USER_ANSWER,
      CORRECT_ANSWER,
      REMARK,
      NOT_ATTEMPTED,
    } = item || {};

    return (
      <Fragment>
        {IS_CORRECT === true ? (
          <section
            className="questions_container1"
            onClick={() => {
              setShowMarks(false);
              setIsReviewMode(true);
              setCurrentQIndex(index);
            }}
            style={{ cursor: "pointer" }}
          >
            <img src={assets.ic_correct_tick} alt="correct_tick" />
            <section className="questions_content">
              <h4>{`Question ${index + 1}`}</h4>
              <h5>
                <LatexRenderer text={QUESTION} /> <span><LatexRenderer text={CORRECT_ANSWER} /></span>
              </h5>
              {REMARK && <h5>Remarks: <LatexRenderer text={REMARK} /></h5>}
            </section>
          </section>
        ) : (
          <section
            className={`${
              NOT_ATTEMPTED
                ? "questions_container_not_attempted"
                : "questions_container3"
            }`}
            onClick={() => {
              setShowMarks(false);
              setIsReviewMode(true);
              setCurrentQIndex(index);
            }}
            style={{ cursor: "pointer" }}
          >
            <img
              src={NOT_ATTEMPTED ? assets.ic_unattempt : assets.ic_wrong_close}
              alt="correct_tick"
            />
            <section className="questions_content">
              <h4>{`Question ${index + 1}`}</h4>
              <h5><LatexRenderer text={QUESTION} /></h5>
              <section className="important_info_wrapper">
                <img src={assets.ic_info} alt="info" />
                <section className="important_details">
                  <h5 className="your_ans">Your Answer: <LatexRenderer text={USER_ANSWER || "Not Answered"} /></h5>
                  <h5 className="correct_ans">Correct Answer: <LatexRenderer text={CORRECT_ANSWER} /></h5>
                  {REMARK && <h5>Remarks: <LatexRenderer text={REMARK} /></h5>}
                </section>
              </section>
            </section>
          </section>
        )}
      </Fragment>
    );
  };

  return (
    <Fragment>
      {showSetupPopup && <QuizSetupPopup />}
      <section className="chat_layout">
        {!quizInitial && !joinQuiz && !hidePanals && (
          <section className="nav_context_contanier">
            <section className="nav_learn_title">
              <h4>Learning Context</h4>
              <h6>Set your topic to start learning</h6>
            </section>

            <section className="nav_subject_details">
              <section className="board_wrapper">
                <section className="class">
                  <h5>Competitive Exam</h5>
                  <h4>{user?.data?.class}</h4>
                </section>
              </section>
            </section>

            <section className="quiz_nav_mode_wrapper">
              <section className="quiz_nav_mode_container">
                <h4>Instant Mode</h4>
                <ToggleButton isToggle={mode} onToggle={onToggleQuizBtn} />
                <h4>Review Mode</h4>
              </section>
            </section>

            <section className="quiz_default_medium_wrapper">
              <h4>Medium</h4>
              <h5>{user?.data?.medium}</h5>
            </section>

            <section className="nav_subject_wrapper">
              {/* MEDIUM */}
              {/* <section
                className="desc_subject_wrapper"
                ref={mediumRef}
                onClick={() => setOpenDropdown("medium")}
              >
                <h4>Medium</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedMedium || "Select Medium"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "medium" && (
                  <section className="desc_options_list">
                    {isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading mediums...
                      </p>
                    ) : mediums?.length > 0 ? (
                      mediums?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMedium(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No mediums found
                      </p>
                    )}
                    {}
                  </section>
                )}
              </section> */}

              {/* SUBJECT */}
              {/* <section
              className="desc_subject_wrapper"
              ref={partRef}
              onClick={() => setOpenDropdown("part")}
            >
              <h4>Part</h4>
              <section className="desc_drop_down_wrapper">
                <p>{selectedPart || "Select Part"}</p>
                <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
              </section>
              {openDropdown === "part" && (
                <section className="desc_options_list">
                  {!selectedMedium ? ( <p
                      style={{
                        textAlign: "center",
                        marginTop: "35%",
                        color: "green",
                      }}
                    >
                      Please first Choose Medium{" "}
                    </p>) : isLoading ? (<p
                      style={{
                        textAlign: "center",
                        marginTop: "35%",
                        color: "green",
                      }}
                    >
                      Loading parts...
                    </p>) :  parts?.length > 0 ? (
                    parts?.map((item, i) => (
                      <p
                        key={i}
                        className="desc_option_item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSubject(item);
                          setOpenDropdown(null);
                        }}
                      >
                        {item}
                      </p>
                    ))
                  ): (<p
                      style={{
                        textAlign: "center",
                        marginTop: "35%",
                        color: "red",
                      }}
                    >
                      No parts found
                    </p>)}
                </section>
              )}
            </section> */}
              <section
                className="desc_subject_wrapper"
                ref={partRef}
                onClick={() => setOpenDropdown("part")}
              >
                <h4>Part</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedPart || "Select Part"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "part" && (
                  <section className="desc_options_list">
                    {isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading parts...
                      </p>
                    ) : parts?.length > 0 ? (
                      parts?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPart(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No parts found
                      </p>
                    )}
                  </section>
                )}
              </section>

              <section
                className="desc_subject_wrapper"
                ref={unitRef}
                onClick={() => setOpenDropdown("unit")}
              >
                <h4>Unit</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedUnit || "Select Unit"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "unit" && (
                  <section className="desc_options_list">
                    {!selectedPart ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Part
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading units...
                      </p>
                    ) : units.length > 0 ? (
                      units?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUnit(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No units found
                      </p>
                    )}
                  </section>
                )}
              </section>

              {/* CHAPTER */}
              <section
                className="desc_subject_wrapper"
                ref={chapterRef}
                onClick={() => setOpenDropdown("chapter")}
              >
                <h4>Chapter</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedChapter || "Select Chapter"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "chapter" && (
                  <section className="desc_options_list">
                    {!selectedUnit ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Unit
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading chapters...
                      </p>
                    ) : chapters?.length > 0 ? (
                      chapters?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            // e.stopPropagation();
                            // setSelectedChapters(item);
                            // setOpenDropdown(null);
                            e.stopPropagation();
                            setSelectedChapter(item);
                            setSelectedChapters([item]);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p className="desc_option_item">No chapters found</p>
                    )}
                    {/* {selectedSubject == "" && <p style={{textAlign:"center",marginTop:"35%",color:"green"}}>Please first Choose Subject</p>}
                  {} */}
                  </section>
                )}
              </section>
              <section
                className="desc_subject_wrapper"
                ref={lessonRef}
                onClick={() => setOpenDropdown("lesson")}
              >
                <h4>Lesson</h4>
                <section className="desc_drop_down_wrapper">
                  {/* <p>{selectedLesson || "Select Lessons"}</p> */}
                  <section className="selected_chapter_list">
                    {selectLessons?.length === 0 ? (
                      <p>Select Lessons</p>
                    ) : (
                      selectLessons?.map((item, index) => {
                        return (
                          <section key={index} className="items">
                            <p>{item}</p>
                            <img
                              src={assets.ic_close_white}
                              alt="ic_close_white"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLessons((prev) => {
                                  if (prev.includes(item)) {
                                    return prev.filter((c) => c !== item);
                                  } else {
                                    return [...prev, item];
                                  }
                                });
                              }}
                              style={{
                                cursor: "pointer",
                                width: "14px",
                                height: "14px",
                              }}
                            />
                          </section>
                        );
                      })
                    )}
                  </section>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "lesson" && (
                  <section className="desc_options_list">
                    {!selectedChapter ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Chapter
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading lessons...
                      </p>
                    ) : lessons.length > 0 ? (
                      lessons?.map((item, i) => (
                        <p
                          key={i}
                          className={`option_item ${selectLessons.includes(item) ? "selected" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLesson(item);
                            setSelectedLessons((prev) => {
                              // if (prev.includes(item)) {
                              //   // remove if already selected
                              //   return prev.filter((c) => c !== item);
                              // } else {
                              //   // add if not selected
                              //   return [...prev, item];
                              // }
                              if (item === "All Lessons") {
                                if (prev.includes("All Lessons")) {
                                  return [];
                                }
                                return ["All Lessons"];
                              }
                              let updated = prev.filter(
                                (c) => c !== "All Lessons",
                              );
                              if (updated.includes(item)) {
                                updated = updated.filter((c) => c !== item);
                              } else {
                                updated = [...updated, item];
                              }
                              return updated;
                            });
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                          {selectLessons.includes(item) && ` \u2713`}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No lessons found
                      </p>
                    )}
                  </section>
                )}
              </section>
              {/* NO OF QUESTIONS */}
              <section
                className="desc_subject_wrapper"
                ref={questionRef}
                onClick={() => setOpenDropdown("question")}
              >
                <h4>No.of questions</h4>
                <section className="desc_drop_down_wrapper">
                  <p>{selectedQuestion || "Select question"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "question" && (
                  <section className="desc_options_list">
                    {!selectLessons ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Lesson
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading no of questions...
                      </p>
                    ) : numberOfQuestion?.length > 0 ? (
                      numberOfQuestion?.map((item, i) => (
                        <p
                          key={i}
                          className="desc_option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedQuestion(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No question found
                      </p>
                    )}
                  </section>
                )}
              </section>

              <button
                className="start_quiz_wrapper"
                disabled={!isStartEnabled}
                onClick={() => setShowSetupPopup(true)}
              >
                {/* Start quiz */}
                Evaluate Me
              </button>
            </section>
          </section>
        )}
        {/* quiz */}
        <section className="chat-panal">
          {quizInitial && !joinQuiz && (
            <section className="quiz_panal_choose_wrapper">
              <section className="quiz_choose_mode_tnpsc_headings">
                <h3>
                  Choose Your <span>Quiz</span> Mode
                </h3>
                <p>
                  Practice at your pace or join a live quiz with others. Select
                  the mode that fits your learning style right now.
                </p>
              </section>
              <section className="quiz_panal_mode_containers_wrapper">
                <section className="quiz_panal_mode_container">
                  <h3>Practice MCQs</h3>
                  <p>
                    Solve questions at your own pace, review your answers, and
                    steadily improve your knowledge without any time pressure.
                  </p>
                  <section className="practice_static_wrapper">
                    {PRACTICE_CONTENT.map((item) => {
                      return (
                        <section className="practice_static">
                          <img src={assets.ic_timer_blue} alt="ic_timer_blue" />
                          <h4>{item}</h4>
                        </section>
                      );
                    })}
                  </section>
                  <section
                    className="practice_btn_wrapper"
                    onClick={() => setQuizInitial(false)}
                  >
                    <h4>Practice Now</h4>
                    <img src={assets.ic_right_arrow} />
                  </section>
                </section>
                <section className="quiz_panal_mode_container">
                  <h3>Join Quiz Room</h3>
                  <p>
                    Compete with others in real-time quizzes hosted by a
                    teacher. Test your speed and accuracy against peers.
                  </p>
                  <section className="practice_static_wrapper">
                    {JOIN_ROOM_CONTENT.map((item) => {
                      return (
                        <section className="practice_static">
                          <img src={assets.ic_timer_blue} alt="ic_timer_blue" />
                          <h4>{item}</h4>
                        </section>
                      );
                    })}
                  </section>
                  <section
                    className="practice_btn_wrapper"
                    onClick={() => {
                      setJoinQuiz(true);
                      console.log("called");
                    }}
                  >
                    <h4>Join Room</h4>
                    <img src={assets.ic_right_arrow} />
                  </section>
                </section>
              </section>
            </section>
          )}

          {!quizInitial && !joinQuiz && (
            <Fragment>
              <div className="quiz-header-premium">
                <div className="header-left">
                  <button
                    className="back-btn"
                    onClick={() => {
                      if (questions.length === 0) {
                        setQuizInitial(true);
                      } else {
                        onCancel();
                      }
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                  </button>
                  <div className="breadcrumbs">
                    <h3>{selectedSubject || "Objective Test"}</h3>
                    <p>{selectedChapter}</p>
                  </div>
                </div>

                {questions.length > 0 && (
                  <div className="header-right">
                    <div className="question-counter">
                      <span className="label">QUESTION</span>
                      <span className="count">
                        <b>{String(currentQIndex + 1).padStart(2, "0")}</b> /{" "}
                        {totalQuestions}
                      </span>
                    </div>
                    {!isReviewMode &&
                      !markDetails?.DETAILS?.length &&
                      questions?.length > 0 && (
                        <div className="nav_hslider_dots">
                          {questions.map((_, i) => (
                            <span
                              key={i}
                              className={`hslider_dot${i === currentQIndex ? " active" : ""}`}
                              onClick={() => setCurrentQIndex(i)}
                            />
                          ))}
                        </div>
                      )}
                    <div className="timer-pill-premium">
                      <svg
                        width="18"
                        height="18"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>{formatTime(timeRemaining)}</span>
                    </div>
                  </div>
                )}
              </div>

              <section className="chat_body_quiz">
                <section className="quiz-main-container">
                  {questions.length === 0 && !markDetails?.DETAILS?.length ? (
                    <QuizHero />
                  ) : (
                    <Fragment>
                      {showPaletteModal ? (
                        <QuestionPalettePage
                          questions={questions}
                          selectedAnswers={selectedAnswers}
                          getQuestionStatus={getQuestionStatus}
                          setCurrentQIndex={setCurrentQIndex}
                          setShowPaletteModal={setShowPaletteModal}
                          setIsQuizActive={setIsQuizActive}
                          onSubmitQuiz={onSubmitQuiz}
                          submitLoading={submitLoading}
                          setIsReviewMode={setIsReviewMode}
                        />
                      ) : showMarks && markDetails?.DETAILS?.length ? (
                        <Fragment>
                          <section className="award_container">
                            <img
                              src={assets.ic_award}
                              alt="award"
                              className="award_img"
                            />
                            <h6 className="compelete_text">
                              You've successfully completed the assessment
                            </h6>
                            <h5 className="select_chapter">
                              {selectedChapter}
                            </h5>
                            <div className="syllabus">
                              <span className="badge_pill">
                                {user?.data?.board}
                              </span>
                              <span className="dot_sep"></span>
                              <span className="badge_pill subject">
                                {selectedPart}
                              </span>
                            </div>
                            <section className="score_box_container">
                              <section className="score_box_wrapper">
                                <img src={assets.ic_score} alt="ic_score" />
                                <h3>{`${scorePercentage}%`}</h3>
                                <h5>Score</h5>
                              </section>
                              <section className="score_box_wrapper">
                                <img src={assets.ic_correct} alt="ic_score" />
                                <h3>{correctCount}</h3>
                                <h5>Correct</h5>
                              </section>
                              <section className="score_box_wrapper">
                                <img src={assets.ic_wrong} alt="ic_wrong" />
                                <h3>{wrongCount}</h3>
                                <h5>In Correct</h5>
                              </section>
                              <section className="score_box_wrapper">
                                <img
                                  src={assets.ic_unattempt}
                                  alt="ic_unattempt"
                                />
                                <h3>{notAnswerdCount}</h3>
                                <h5>Not Attempted</h5>
                              </section>
                            </section>
                            <section className="questions_overview_wrapper">
                              <h4>Question by Question Review</h4>
                              <h6>Detailed breakdown of each question</h6>
                              {markDetails?.DETAILS.map(renderScoreCardDetails)}
                            </section>
                            <section className="quiz_btns_wrapper">
                              <button
                                className="retake_btn"
                                onClick={onRetakeQuiz}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                                <span>Retake Quiz</span>
                              </button>
                              <button
                                className="back_menu_btn"
                                onClick={onCancel}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                                  />
                                </svg>
                                <span>Back to Main Menu</span>
                              </button>
                            </section>
                          </section>
                        </Fragment>
                      ) : (
                        <div
                          className={
                            isReviewMode
                              ? "quiz_review_layout"
                              : "quiz_normal_layout"
                          }
                        >
                          <div className="review_main_content">
                            {(() => {
                              const showStack =
                                !isReviewMode &&
                                !markDetails?.DETAILS?.length &&
                                questions?.length > 0;

                              if (showStack) {
                                const q = questions[currentQIndex];
                                const selOpt = selectedAnswers[currentQIndex];

                                return (
                                  <QuizHSlider
                                    questions={questions}
                                    renderCard={(qIdx) => {
                                      const qItem = questions[qIdx];
                                      const sOpt = selectedAnswers[qIdx];
                                      const isAns = sOpt !== undefined;
                                      const isCor =
                                        isAns &&
                                        qItem?.options[sOpt] === qItem?.answer;
                                      return (
                                        <QuestionCardContent
                                          q={qItem}
                                          qIndex={qIdx}
                                          currentQIndex={currentQIndex}
                                          isFullySubmitted={isFullySubmitted}
                                          isAnsweredQ={isAns}
                                          isCorrectQ={isCor}
                                          selOpt={sOpt}
                                          mode={mode}
                                          handleOptionSelect={
                                            handleOptionSelect
                                          }
                                          showStack={showStack}
                                          isReviewMode={isReviewMode}
                                          setCurrentQIndex={setCurrentQIndex}
                                          setShowPaletteModal={
                                            setShowPaletteModal
                                          }
                                          questions={questions}
                                          questionDetails={questionDetails}
                                        />
                                      );
                                    }}
                                    currentQIndex={currentQIndex}
                                    setCurrentQIndex={setCurrentQIndex}
                                    onEndQuiz={() => setShowPaletteModal(true)}
                                    endIcon={assets.ic_arrow_white}
                                  />
                                );
                              }

                              if (
                                isReviewMode ||
                                (!markDetails?.DETAILS?.length &&
                                  questionDetails.length > 0)
                              ) {
                                const q = questions[currentQIndex];
                                const selOpt = selectedAnswers[currentQIndex];
                                const isAnsweredQ = selOpt !== undefined;
                                const isCorrectQ =
                                  isAnsweredQ &&
                                  q?.options[selOpt] === q?.answer;

                                return (
                                  <section
                                    className="quiz-container"
                                    key={animKey}
                                  >
                                    <section
                                      className={`quiz-main slide-in-${slideDirection}`}
                                    >
                                      <QuestionCardContent
                                        q={q}
                                        qIndex={currentQIndex}
                                        currentQIndex={currentQIndex}
                                        isFullySubmitted={isFullySubmitted}
                                        isAnsweredQ={isAnsweredQ}
                                        isCorrectQ={isCorrectQ}
                                        selOpt={selOpt}
                                        mode={mode}
                                        handleOptionSelect={handleOptionSelect}
                                        showStack={showStack}
                                        isReviewMode={isReviewMode}
                                        setCurrentQIndex={setCurrentQIndex}
                                        setShowPaletteModal={
                                          setShowPaletteModal
                                        }
                                        questions={questions}
                                        questionDetails={questionDetails}
                                        onZoomImage={setZoomModalImage}
                                      />
                                    </section>
                                  </section>
                                );
                              }

                              return null;
                            })()}
                          </div>

                          {isReviewMode && (
                            <QuestionPaletteSidebar
                              questions={questions}
                              selectedAnswers={selectedAnswers}
                              getQuestionStatus={getQuestionStatus}
                              currentQIndex={currentQIndex}
                              setCurrentQIndex={setCurrentQIndex}
                              isQuizSubmitted={isFullySubmitted}
                              onSubmitQuiz={() => {
                                setIsQuizActive(false);
                                onSubmitQuiz();
                              }}
                              submitLoading={submitLoading}
                            />
                          )}
                        </div>
                      )}
                    </Fragment>
                  )}
                </section>
              </section>
            </Fragment>
          )}

          {showJoinScreen && (
            <QuizJoinInitialContent
              roomCode={roomCode}
              nickName={nickName}
              onChangeRoom={onChangeRoom}
              waitingCall={waitingCall}
              setWaitingCall={setWaitingCall}
              joinRoom={joinRoom}
              setJoinQuiz={setJoinQuiz}
            />
          )}
          {waitingCall && (
            <QuizLobbyContent
              players={players}
              roomCode={roomCode}
              nickName={nickName}
              setJoinQuiz={setJoinQuiz}
              setWaitingCall={setWaitingCall}
            />
          )}
          {wsQuizStarted &&
            wsQuestion &&
            wsQuizFinalResults?.length === 0 &&
            !wsQuizEnded && (
              <section style={{ background: "#E5E7EB", overflowY: "scroll" }}>
                <div className="ws_quiz_container">
                  <div className="ws_quiz_header">
                    <div className="ws_question_count">
                      Q - {wsQuestionIndex + 1}/{wsTotalQuestions}
                    </div>

                    <div className="ws_timer_badge">
                      00:{String(wsTimeRemaining).padStart(2, "0")}
                    </div>
                  </div>

                  <div className="ws_progress_wrapper">
                    <div
                      className="ws_progress_bar"
                      style={{
                        width: `${((wsQuestionIndex + 1) / wsTotalQuestions) * 100}%`,
                      }}
                    />
                  </div>

                  <div className="ws_question_title">
                    <LatexRenderer text={wsQuestion?.question} />
                  </div>

                  {wsQuestion?.imageUrls && wsQuestion.imageUrls.length > 0 && (
                    <div className="ws_q_image_container">
                      {wsQuestion.imageUrls.map((imgUrl, i) => (
                        <QuizImagePreview
                          key={i}
                          src={imgUrl}
                          alt={`Question Diagram ${i + 1}`}
                          title={`Question ${wsQuestionIndex + 1} Diagram`}
                          onZoom={setZoomModalImage}
                        />
                      ))}
                    </div>
                  )}

                  {(wsQuestion?.isShort || wsQuestion?.type === "SHORT" || wsQuestion?.questionType === "SHORT") ? (
                    <div className="ws_short_answer_wrapper" style={{ marginTop: "24px", marginBottom: "20px" }}>
                      <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, color: "#374151", fontSize: "14px" }}>
                        Type your answer below:
                      </label>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <textarea
                          rows={3}
                          className="ws_short_answer_input"
                          placeholder="Enter your answer here..."
                          disabled={wsSubmitted}
                          value={wsSelectedAnswer ?? ""}
                          onChange={(e) => setWsSelectedAnswer(e.target.value)}
                          style={{
                            flex: 1,
                            padding: "14px 18px",
                            fontSize: "16px",
                            border: "2px solid #612EF7",
                            borderRadius: "12px",
                            outline: "none",
                            backgroundColor: wsSubmitted ? "#F3F4F6" : "#FFFFFF",
                            color: "#1F2937",
                            minHeight: "90px",
                            maxHeight: "180px",
                            overflowY: "auto",
                            resize: "vertical",
                            fontFamily: "inherit",
                            lineHeight: "1.5",
                          }}
                        />
                        <button
                          type="button"
                          disabled={wsSubmitted || !wsSelectedAnswer || String(wsSelectedAnswer).trim() === ""}
                          onClick={() => handleWsAnswer(wsSelectedAnswer)}
                          style={{
                            padding: "14px 24px",
                            fontSize: "15px",
                            fontWeight: 600,
                            color: "#FFFFFF",
                            backgroundColor: (wsSubmitted || !wsSelectedAnswer || String(wsSelectedAnswer).trim() === "") ? "#9CA3AF" : "#612EF7",
                            borderRadius: "12px",
                            border: "none",
                            cursor: (wsSubmitted || !wsSelectedAnswer || String(wsSelectedAnswer).trim() === "") ? "not-allowed" : "pointer",
                          }}
                        >
                          {wsSubmitted ? "Submitted" : "Submit"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ws_options_wrapper">
                      {wsQuestion?.options?.map((option, index) => {
                        const optionLetter = String.fromCharCode(65 + index);
                        const optImg = wsQuestion?.optionImages?.[optionLetter];

                        return (
                          <button
                            key={index}
                            onClick={() => handleWsAnswer(index)}
                            className={`ws_option_btn ${
                              wsSelectedAnswer === index ? "active" : ""
                            }`}
                          >
                            <div className="ws_option_left">
                              <div className="ws_radio">
                                {wsSelectedAnswer === index && (
                                  <div className="ws_radio_inner"></div>
                                )}
                              </div>

                              <span className="ws_option_letter">
                                {optionLetter}.
                              </span>

                              <div className="ws_opt_content">
                                {option && option.trim() !== "" && (
                                  <span className="ws_option_text"><LatexRenderer text={option} /></span>
                                )}
                                {optImg && (
                                  <div className="ws_opt_img_wrapper">
                                    <QuizImagePreview
                                      src={optImg}
                                      alt={`Option ${optionLetter}`}
                                      title={`Question ${wsQuestionIndex + 1} - Option ${optionLetter}`}
                                      isOption
                                      onZoom={setZoomModalImage}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            )}

          {showAdminRoundResult && (
            <CommonPopup
              open={showAdminRoundResult}
              onClose={() => setShowAdminRoundResult(false)}
              url={true}
            >
              <AdminRoundResult data={adminRoundResult} />
            </CommonPopup>
          )}

          {
            // (wsQuizEnded && wsQuizFinalResults?.length > 0) ||
            showAsyncLeaderboard &&
              asyncLeaderboard?.length > 0 &&
              !showAsyncHistory && (
                <section className="quiz_leader_wrapper">
                  <div className="quiz_leader_board_main_heading">
                    <div className="leaderboard_trophy_badge">
                      <span>🏆 Final Standings</span>
                    </div>
                    <h3>
                      Final <span>Scores</span>
                    </h3>
                    <h5>Incredible performance! Here are the final results.</h5>
                  </div>

                  <div className="leaderboard">
                    {leaderboardData?.slice(0, 3).map((user, index) => (
                      <div className={`card position-${index + 1}`} key={index}>
                        <div className="avatar-wrapper">
                          <img
                            src={assets.ic_chat_person}
                            alt={user?.nickname}
                          />
                          <span className={`badge rank-${index + 1}`}>
                            #{index + 1}
                          </span>
                        </div>

                        <div className="card-content">
                          <h3>{user?.nickname}</h3>
                          <p className="score">{user?.score} pts</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="quiz_leader_board_table_wrapper">
                    <div className="quiz_leader_board_header">
                      <div className="leader_board_sub_header">
                        <h4>Rank</h4>
                        <h4>Player</h4>
                      </div>
                      <h4>Score</h4>
                    </div>

                    {leaderboardData?.map((item, index) => {
                      return (
                        <div
                          key={index}
                          className="quiz_leader_board_card_container"
                        >
                          <div className="leader_board_no">
                            <h4>{index + 1}</h4>
                          </div>

                          <div className="leader_board_inner_wrapper">
                            <img
                              src={assets.ic_chat_person}
                              alt="ic_chat_person"
                            />
                            <h4>{item?.nickname}</h4>
                          </div>

                          <div className="leader_board_score">
                            <h4>{item?.score} pts</h4>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="quiz_leader_board_btns_wrapper">
                    <button
                      className="leader_board_quiz_btn"
                      onClick={() => {
                        setShowAsyncHistory(true);
                        apiJoinRoomHistoryDetails();
                      }}
                    >
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Quiz History</span>
                    </button>

                    <button
                      className="leader_board_new_room_btn"
                      onClick={() => {
                        // live reset
                        setJoinQuiz(true);
                        setWsQuizEnded(false);
                        setWsQuizStarted(false);
                        setWsQuestion(null);
                        setWsFinalScores([]);
                        setWaitingCall(false);
                        setPlayers([]);
                        setWsSelectedAnswer(null);

                        // async reset
                        setIsAsyncQuiz(false);
                        setAsyncQuestions([]);
                        setAsyncCurrentIndex(0);
                        setAsyncSelectedAnswers({});
                        setShowReviewPage(false);
                        setAsyncLeaderboard([]);
                        setShowAsyncLeaderboard(false);
                        setShowAsyncHistory(false);

                        // reset room
                        setRoomDetails({
                          roomCode: "",
                          nickName: "",
                        });
                        document.body.classList.remove("hide_sidebar");
                        if (document.fullscreenElement) {
                          document.exitFullscreen().catch(() => {});
                        }
                      }}
                    >
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                      </svg>
                      <span>New Room</span>
                    </button>
                  </div>
                </section>
              )
          }

          {isAsyncQuiz && asyncCurrentQuestion && !showReviewPage && (
            <section style={{ background: "#E5E7EB", overflowY: "scroll" }}>
              <div className="ws_quiz_container">
                <div className="ws_quiz_header">
                  <div className="ws_question_count">
                    Q - {asyncCurrentIndex + 1}/{wsTotalQuestions}
                  </div>
                </div>

                <div className="ws_progress_wrapper">
                  <div
                    className="ws_progress_bar"
                    style={{
                      width: `${((asyncCurrentIndex + 1) / wsTotalQuestions) * 100}%`,
                    }}
                  />
                </div>

                <div className="ws_question_title">
                  <LatexRenderer text={asyncCurrentQuestion?.question} />
                </div>

                {asyncCurrentQuestion?.imageUrls && asyncCurrentQuestion.imageUrls.length > 0 && (
                  <div className="ws_q_image_container">
                    {asyncCurrentQuestion.imageUrls.map((imgUrl, i) => (
                      <QuizImagePreview
                        key={i}
                        src={imgUrl}
                        alt={`Question Diagram ${i + 1}`}
                        title={`Question ${asyncCurrentIndex + 1} Diagram`}
                        onZoom={setZoomModalImage}
                      />
                    ))}
                  </div>
                )}

                {(asyncCurrentQuestion?.isShort || asyncCurrentQuestion?.type === "SHORT" || asyncCurrentQuestion?.questionType === "SHORT") ? (
                  <div className="ws_short_answer_wrapper" style={{ marginTop: "24px", marginBottom: "20px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, color: "#374151", fontSize: "14px" }}>
                      Type your answer below:
                    </label>
                    <textarea
                      rows={3}
                      className="ws_short_answer_input"
                      placeholder="Enter your answer here..."
                      value={asyncSelectedAnswers[asyncCurrentIndex] ?? ""}
                      onChange={(e) => handleAsyncAnswer(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "14px 18px",
                        fontSize: "16px",
                        border: "2px solid #612EF7",
                        borderRadius: "12px",
                        outline: "none",
                        backgroundColor: "#FFFFFF",
                        color: "#1F2937",
                        boxShadow: "0 2px 4px rgba(97, 46, 247, 0.08)",
                        minHeight: "90px",
                        maxHeight: "180px",
                        overflowY: "auto",
                        resize: "vertical",
                        fontFamily: "inherit",
                        lineHeight: "1.5",
                      }}
                    />
                  </div>
                ) : (
                  <div className="ws_options_wrapper">
                    {asyncCurrentQuestion?.options?.map((option, index) => {
                      const optionLetter = String.fromCharCode(65 + index);
                      const optImg = asyncCurrentQuestion?.optionImages?.[optionLetter];

                      return (
                        <button
                          key={index}
                          onClick={() => handleAsyncAnswer(index)}
                          className={`ws_option_btn ${
                            asyncSelectedAnswers[asyncCurrentIndex] === index
                              ? "active"
                              : ""
                          }`}
                        >
                          <div className="ws_option_left">
                            <div className="ws_radio">
                              {asyncSelectedAnswers[asyncCurrentIndex] ===
                                index && <div className="ws_radio_inner"></div>}
                            </div>

                            <span className="ws_option_letter">
                              {optionLetter}.
                            </span>

                            <div className="ws_opt_content">
                              {option && option.trim() !== "" && (
                                <span className="ws_option_text"><LatexRenderer text={option} /></span>
                              )}
                              {optImg && (
                                <div className="ws_opt_img_wrapper">
                                  <QuizImagePreview
                                    src={optImg}
                                    alt={`Option ${optionLetter}`}
                                    title={`Question ${asyncCurrentIndex + 1} - Option ${optionLetter}`}
                                    isOption
                                    onZoom={setZoomModalImage}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                <button className="nexts_btn_wrapper" onClick={handleAsyncNext}>
                  {editingFromReview
                    ? "Back to Review"
                    : asyncCurrentIndex === (wsTotalQuestions ? wsTotalQuestions - 1 : asyncQuestions.length - 1)
                    ? "Review Answers"
                    : "Next Question"}
                </button>
              </div>
            </section>
          )}

          {showReviewPage && (
            <QuizReviewContent
              questions={asyncQuestions}
              selectedAnswers={asyncSelectedAnswers}
              setAsyncCurrentIndex={setAsyncCurrentIndex}
              setShowReviewPage={setShowReviewPage}
              setEditingFromReview={setEditingFromReview}
              onSubmitQuiz={onSubmitQuiz}
              showReviewPage={showReviewPage}
              asyncQuizSubmit={asyncQuizSubmit}
            />
          )}

          {showAsyncHistory && (
            <QuizHistroyDetails
              historyData={quizHistorySessions}
              onBack={() => setShowAsyncHistory(false)}
            />
          )}

          {isNeedsFullscreen && (
            <LiveQuizFullscreenGuard onReEnter={tryFullscreen} />
          )}

          {showRulesModal && (
            <AntiCheatRulesModal
              maxWarnings={3}
              onStart={acknowledgeRules}
            />
          )}

          {showAntiCheatModal && (
            <AntiCheatModal
              warnings={antiCheatWarnings}
              maxWarnings={3}
              message={antiCheatMessage}
              onResume={reEnterFullscreen}
            />
          )}

          {isAutoSubmitted && (
            <AutoSubmittedModal
              reason={autoSubmitReason}
              onClose={closeAutoSubmitModal}
            />
          )}

          <ImageZoomModal
            image={zoomModalImage}
            onClose={() => setZoomModalImage(null)}
          />
        </section>
      </section>
    </Fragment>
  );
};
